import type { ChangeEvent, ReactNode } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { Formik, type FormikHelpers } from 'formik';
import { boolean, object, string } from 'yup';
import MultiSelect from '@/components/common/MultiSelect';
import { toPersianDigit } from '@/helpers/utils';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import '@/assets/ai-content.css';

export type AiAnalysisSource = 'url' | 'content';

export type AiAnalysisFormValues = {
  source: AiAnalysisSource;
  url: string;
  content: string;
  goal: string;
  keyword: string;
  analyzeSeo: boolean;
  analyzeAi: boolean;
  analyzeEeat: boolean;
  analyzeGeo: boolean;
};

type AiAnalysisFormProps = {
  onSubmit: (
    values: AiAnalysisFormValues,
    actions: FormikHelpers<AiAnalysisFormValues>
  ) => void | Promise<void>;
  isSubmitting: boolean;
  initialValues?: Partial<AiAnalysisFormValues>;
};

type AnalysisOption = {
  value: 'seo' | 'ai' | 'eeat' | 'geo';
  label: string;
};

const MIN_CONTENT_LENGTH = 80;
const MAX_CONTENT_LENGTH = 20000;

const ANALYSIS_OPTIONS: AnalysisOption[] = [
  { value: 'seo', label: 'تحلیل سئو' },
  { value: 'ai', label: 'تشخیص AI' },
  { value: 'eeat', label: 'تحلیل EEAT' },
  { value: 'geo', label: 'تحلیل GEO' }
];

const initialValues: AiAnalysisFormValues = {
  source: 'url',
  url: '',
  content: '',
  goal: '',
  keyword: '',
  analyzeSeo: true,
  analyzeAi: true,
  analyzeEeat: true,
  analyzeGeo: true
};

const validationSchema = object()
  .shape({
    source: string().oneOf(['url', 'content']).required(),
    url: string().when('source', {
      is: 'url',
      then: schema =>
        schema
          .url('آدرس صفحه معتبر نیست')
          .required('وارد کردن آدرس صفحه الزامی است'),
      otherwise: schema => schema.notRequired()
    }),
    content: string().when('source', {
      is: 'content',
      then: schema =>
        schema
          .min(
            MIN_CONTENT_LENGTH,
            `متن محتوا باید حداقل ${toPersianDigit(`${MIN_CONTENT_LENGTH}`)} کاراکتر باشد`
          )
          .max(
            MAX_CONTENT_LENGTH,
            `متن محتوا نباید بیشتر از ${toPersianDigit(`${MAX_CONTENT_LENGTH}`)} کاراکتر باشد`
          )
          .required('وارد کردن متن محتوا الزامی است'),
      otherwise: schema => schema.notRequired()
    }),
    goal: string().when('analyzeSeo', {
      is: true,
      then: schema =>
        schema
          .min(10, 'هدف محتوا باید حداقل ۱۰ کاراکتر باشد')
          .required('وارد کردن هدف محتوا الزامی است'),
      otherwise: schema => schema.notRequired()
    }),
    keyword: string().when('analyzeSeo', {
      is: true,
      then: schema =>
        schema
          .min(2, 'کلمه کلیدی باید حداقل ۲ کاراکتر باشد')
          .required('وارد کردن کلمه کلیدی الزامی است'),
      otherwise: schema => schema.notRequired()
    }),
    analyzeSeo: boolean().required(),
    analyzeAi: boolean().required(),
    analyzeEeat: boolean().required(),
    analyzeGeo: boolean().required()
  })
  .test({
    name: 'at-least-one-analysis',
    test(values) {
      if (
        values?.analyzeSeo ||
        values?.analyzeAi ||
        values?.analyzeEeat ||
        values?.analyzeGeo
      ) {
        return true;
      }
      return this.createError({
        path: 'analyzeSeo',
        message: 'حداقل یکی از تحلیل‌ها را انتخاب کنید'
      });
    }
  });

const FieldLabel = ({
  children,
  tooltip,
  className = 'mb-1'
}: {
  children: ReactNode;
  tooltip: string;
  className?: string;
}) => (
  <Form.Label className={`fs--1 d-inline-flex align-items-center lh-sm ${className}`}>
    {children}
    <HelpTooltip placement="top" html={tooltip} />
  </Form.Label>
);

const FieldError = ({ error }: { error?: string }) =>
  error ? (
    <small className="fs--2 mt-1 d-block text-danger">{error}</small>
  ) : null;

const getSelectedAnalysis = (values: AiAnalysisFormValues) =>
  ANALYSIS_OPTIONS.filter(
    option =>
      (option.value === 'seo' && values.analyzeSeo) ||
      (option.value === 'ai' && values.analyzeAi) ||
      (option.value === 'eeat' && values.analyzeEeat) ||
      (option.value === 'geo' && values.analyzeGeo)
  );

const AiAnalysisForm = ({
  onSubmit,
  isSubmitting,
  initialValues: initialValuesOverride
}: AiAnalysisFormProps) => (
  <Row className="mb-3">
    <Col lg={12}>
      <Formik<AiAnalysisFormValues>
        onSubmit={onSubmit}
        enableReinitialize
        initialValues={{ ...initialValues, ...initialValuesOverride }}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting: isFormikSubmitting
        }) => {
          const isUrlMode = values.source === 'url';
          const contentLength = values.content.trim().length;
          const urlError = touched.url ? errors.url : undefined;
          const contentError = touched.content ? errors.content : undefined;
          const hideSeoFields = !values.analyzeSeo;
          const goalError = touched.goal ? errors.goal : undefined;
          const keywordError = touched.keyword ? errors.keyword : undefined;
          const analysisError =
            touched.analyzeSeo ||
            touched.analyzeAi ||
            touched.analyzeEeat ||
            touched.analyzeGeo
              ? errors.analyzeSeo
              : undefined;

          const handleSourceChange = (
            event: ChangeEvent<HTMLSelectElement>
          ) => {
            setFieldValue('source', event.target.value);
            setFieldTouched('url', false);
            setFieldTouched('content', false);
          };

          const handleAnalysisChange = (selected: unknown) => {
            const next = (selected as AnalysisOption[] | null) || [];
            setFieldTouched('analyzeSeo', true);
            setFieldTouched('analyzeAi', true);
            setFieldTouched('analyzeEeat', true);
            setFieldTouched('analyzeGeo', true);
            if (next.length === 0) return;
            setFieldValue(
              'analyzeSeo',
              next.some(option => option.value === 'seo')
            );
            setFieldValue(
              'analyzeAi',
              next.some(option => option.value === 'ai')
            );
            setFieldValue(
              'analyzeEeat',
              next.some(option => option.value === 'eeat')
            );
            setFieldValue(
              'analyzeGeo',
              next.some(option => option.value === 'geo')
            );
          };

          return (
            <Form className="mt-3 mb-2" onSubmit={handleSubmit}>
              <div
                className={`ai-analysis-form-row${
                  hideSeoFields ? ' ai-analysis-form-row--ai-only' : ''
                }`}
              >
                <Form.Group controlId="ai-source" className="ai-field-source">
                  <FieldLabel tooltip="آدرس صفحه منتشرشده را وارد کنید. ورود مستقیم متن محتوا فعلا در دسترس نیست.">
                    نوع منبع
                  </FieldLabel>
                  <Form.Select
                    size="sm"
                    aria-label="نوع منبع تحلیل"
                    value={values.source}
                    onChange={handleSourceChange}
                  >
                    <option value="url">آدرس صفحه</option>
                    <option value="content" disabled>
                      متن محتوا
                    </option>
                  </Form.Select>
                </Form.Group>

                {isUrlMode ? (
                  <Form.Group controlId="ai-url" className="ai-field-url">
                    <FieldLabel tooltip="آدرس دقیق همان صفحه‌ای که می‌خواهید تحلیل شود را وارد کنید.">
                      نشانی صفحه
                    </FieldLabel>
                    <div dir="ltr">
                      <Form.Control
                        type="url"
                        name="url"
                        size="sm"
                        dir="ltr"
                        className="text-start"
                        placeholder="https://example.com/page"
                        value={values.url}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={!!urlError}
                      />
                    </div>
                    <FieldError error={urlError} />
                  </Form.Group>
                ) : null}

                <Form.Group controlId="ai-analysis" className="ai-field-analysis">
                  <FieldLabel tooltip="حداقل یکی از تحلیل‌ها را انتخاب کنید.<br/><strong>تحلیل سئو:</strong> امتیاز سئو، معیارها و سلامت صفحه را بررسی می‌کند.<br/><strong>تشخیص AI:</strong> میزان شباهت متن به نوشتار انسان یا هوش مصنوعی را تخمین می‌زند.<br/><strong>تحلیل EEAT:</strong> تجربه، تخصص، اعتبار و اعتماد محتوا را ارزیابی می‌کند.<br/><strong>تحلیل GEO:</strong> آمادگی صفحه برای جستجوی مولد و استنادپذیری در پاسخ‌های هوش مصنوعی را می‌سنجد.">
                    نوع تحلیل توسط AI
                  </FieldLabel>
                  <MultiSelect
                    options={ANALYSIS_OPTIONS}
                    value={getSelectedAnalysis(values)}
                    onChange={handleAnalysisChange}
                    onBlur={() => {
                      setFieldTouched('analyzeSeo', true);
                      setFieldTouched('analyzeAi', true);
                      setFieldTouched('analyzeEeat', true);
                      setFieldTouched('analyzeGeo', true);
                    }}
                    placeholder="انتخاب کنید"
                    isSearchable={false}
                    isClearable={false}
                    hideSelectedOptions={false}
                    classNamePrefix="react-select"
                    className={analysisError ? 'is-invalid' : undefined}
                    styles={{
                      control: base => ({
                        ...base,
                        minHeight: '31px',
                        fontSize: '0.8125rem',
                        boxShadow: 'none',
                        cursor: 'pointer'
                      }),
                      valueContainer: base => ({
                        ...base,
                        padding: '0 8px',
                        gap: 4
                      }),
                      multiValue: base => ({
                        ...base,
                        margin: 0
                      }),
                      indicatorsContainer: base => ({
                        ...base,
                        height: 31
                      }),
                      dropdownIndicator: base => ({
                        ...base,
                        padding: '4px 8px'
                      }),
                      clearIndicator: base => ({
                        ...base,
                        padding: '4px'
                      }),
                      menu: base => ({
                        ...base,
                        fontSize: '0.8125rem',
                        zIndex: 20
                      })
                    }}
                  />
                  <FieldError error={analysisError} />
                </Form.Group>

                {!hideSeoFields ? (
                  <>
                    <Form.Group controlId="ai-goal" className="ai-field-goal">
                      <FieldLabel tooltip="به زبان ساده بنویسید دقیقا چه هدفی از این صفحه دارید.">
                        هدف محتوا
                      </FieldLabel>
                      <Form.Control
                        type="text"
                        name="goal"
                        size="sm"
                        placeholder="رتبه در کلمات آموزشی..."
                        value={values.goal}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={!!goalError}
                      />
                      <FieldError error={goalError} />
                    </Form.Group>

                    <Form.Group controlId="ai-keyword" className="ai-field-keyword">
                      <FieldLabel tooltip="مهم‌ترین کلمه‌ای که می‌خواهید این صفحه روی آن رتبه بگیرد.">
                        کلمه کلیدی
                      </FieldLabel>
                      <Form.Control
                        type="text"
                        name="keyword"
                        size="sm"
                        placeholder="مثل: لینک‌سازی داخلی"
                        value={values.keyword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={!!keywordError}
                      />
                      <FieldError error={keywordError} />
                    </Form.Group>
                  </>
                ) : null}

                <div className="ai-field-submit">
                  <div className="d-none d-lg-block fs--1 mb-1">&nbsp;</div>
                  <Button
                    variant="falcon-primary"
                    type="submit"
                    size="sm"
                    className="px-4 d-flex align-items-center justify-content-center w-100 w-lg-auto"
                    disabled={isSubmitting || isFormikSubmitting}
                  >
                    {isSubmitting || isFormikSubmitting ? (
                      <>
                        <Spinner
                          style={{
                            width: '16px',
                            height: '16px'
                          }}
                          className="me-1"
                        />
                        ارسال...
                      </>
                    ) : (
                      'ارسال'
                    )}
                  </Button>
                </div>
              </div>

              {!isUrlMode ? (
                <Form.Group controlId="ai-content" className="mt-3">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <FieldLabel
                      className="mb-0"
                      tooltip="برای پیش‌نویس یا صفحه در دسترس‌نبوده، متن را مستقیم وارد کنید."
                    >
                      متن صفحه
                    </FieldLabel>
                    <span
                      className={`fs--2 ${
                        contentError ? 'text-danger' : 'text-500'
                      }`}
                    >
                      {toPersianDigit(`${contentLength}`)} /{' '}
                      {toPersianDigit(`${MAX_CONTENT_LENGTH}`)} کاراکتر
                    </span>
                  </div>
                  <Form.Control
                    as="textarea"
                    name="content"
                    rows={6}
                    size="sm"
                    placeholder="متن کامل صفحه را اینجا بچسبانید..."
                    value={values.content}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={!!contentError}
                    maxLength={MAX_CONTENT_LENGTH}
                  />
                  <FieldError error={contentError} />
                </Form.Group>
              ) : null}
            </Form>
          );
        }}
      </Formik>
    </Col>
  </Row>
);

export default AiAnalysisForm;
